import { ArrowLeft, Volume2, VolumeX, Users, Play, FastForward } from 'lucide-react';
import QRCode from '../common/QRCode';

const CHIP_COLORS = [
  'bg-mp-lime',
  'bg-mp-bg-mint',
  'bg-mp-teal',
  'bg-mp-fox-cream',
  'bg-mp-bg-pink',
  'bg-mp-bg-blue',
];

export default function HostLobby({ sessionCode, participants, onStartGame, onBack, autoAdvance, onAutoAdvanceChange, isMuted, onToggleMute }) {
  const participantList = Object.values(participants || {});
  const canStart = participantList.length > 0;

  return (
    <div className="bg-mp-bg-lightgreen font-body min-h-dvh landscape:h-dvh flex flex-col relative overflow-hidden">
      {/* Top Bar */}
      <header className="relative z-10 flex items-center justify-between p-4 pt-5 max-w-5xl mx-auto w-full">
        <button
          onClick={onBack}
          className="flex items-center justify-center w-10 h-10 rounded-full bg-white text-mp-darkgreen shadow-mp-1 hover:bg-mp-bg-mint transition-colors"
          aria-label="Tilbake"
        >
          <ArrowLeft size={20} />
        </button>
        <span className="mp-eyebrow">Mikkel kviss · Lobby</span>
        <button
          onClick={onToggleMute}
          className="flex items-center justify-center w-10 h-10 rounded-full bg-white text-mp-darkgreen shadow-mp-1 hover:bg-mp-bg-mint transition-colors"
          aria-label={isMuted ? 'Skru på lyd' : 'Skru av lyd'}
        >
          {isMuted ? <VolumeX size={20} /> : <Volume2 size={20} />}
        </button>
      </header>

      {/* Main Content Area */}
      <div className="relative z-10 flex flex-col landscape:flex-row items-center landscape:items-stretch gap-4 landscape:gap-6 w-full max-w-md landscape:max-w-5xl mx-auto px-4 flex-1 min-h-0">
        {/* Left column: PIN + QR */}
        <div className="w-full landscape:w-1/2 flex flex-col items-center landscape:justify-center">
          <div className="w-full text-center mb-4 landscape:mb-3">
            <span className="mp-eyebrow">Bli med på</span>
            <h2 className="font-display text-mp-darkgreen text-2xl mt-1">mikkelparken.no/kviss</h2>
          </div>

          {/* PIN Card */}
          <div className="w-full bg-white rounded-[28px] landscape:rounded-2xl p-6 landscape:p-4 shadow-mp-2 border border-[rgba(17,73,66,0.10)] flex flex-col landscape:flex-row landscape:items-center landscape:gap-4 items-center justify-center mb-4 landscape:mb-0">
            <div className="flex flex-col items-center landscape:items-start landscape:flex-1">
              <span className="mp-eyebrow mb-1">PIN-kode</span>
              <h1 className="font-display text-mp-darkgreen text-[3.5rem] landscape:text-4xl leading-none tracking-[0.06em]">
                {sessionCode?.slice(0, 3)} {sessionCode?.slice(3)}
              </h1>
            </div>
            <div className="mt-4 landscape:mt-0 shrink-0">
              <div className="landscape:hidden">
                <QRCode sessionId={sessionCode} size={120} />
              </div>
              <div className="hidden landscape:block">
                <QRCode sessionId={sessionCode} size={96} />
              </div>
            </div>
          </div>
        </div>

        {/* Right column: Players */}
        <div className="w-full landscape:w-1/2 flex flex-col flex-1 min-h-0">
          <div className="w-full flex items-center justify-between mb-3 px-2">
            <h3 className="font-display text-mp-darkgreen text-lg flex items-center gap-2">
              <span className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-mp-darkgreen text-mp-lime text-xs font-display">
                {participantList.length}
              </span>
              Spelarar klare
            </h3>
          </div>

          <div className="w-full flex-1 overflow-y-auto pb-32 landscape:pb-4 min-h-0">
            {participantList.length === 0 ? (
              <div className="text-center py-8">
                <Users size={56} className="text-mp-stone-300 mx-auto mb-4" />
                <p className="font-body text-mp-stone-700">Ventar på at spelarar skal bli med …</p>
                <p className="font-body text-mp-stone-500 text-sm mt-2">
                  Del PIN-koden ovanfor for å invitere spelarar
                </p>
              </div>
            ) : (
              <div className="flex flex-wrap gap-2.5 justify-center">
                {participantList.map((participant, index) => (
                  <div
                    key={participant.id}
                    className="inline-flex items-center gap-2 rounded-full bg-white border border-[rgba(17,73,66,0.10)] shadow-mp-1 px-3.5 py-2 animate-[slideIn_0.3s_ease-out]"
                  >
                    <span className={`w-2 h-2 rounded-full ${CHIP_COLORS[index % CHIP_COLORS.length]}`} />
                    <p className="font-display text-mp-darkgreen text-sm">{participant.name}</p>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Bottom Action Bar */}
      <div className="fixed landscape:relative landscape:shrink-0 bottom-0 left-0 right-0 p-4 landscape:p-3 pb-8 landscape:pb-4 z-20 bg-gradient-to-t from-mp-bg-lightgreen via-mp-bg-lightgreen to-transparent pt-8 landscape:pt-3">
        <div className="max-w-md landscape:max-w-5xl mx-auto w-full flex flex-col landscape:flex-row landscape:items-center gap-3 landscape:gap-6">
          {/* Auto-advance toggle */}
          {onAutoAdvanceChange && (
            <div className="flex items-center justify-between landscape:justify-start landscape:gap-3 px-1 landscape:px-0 landscape:shrink-0">
              <label htmlFor="auto-advance" className="flex items-center gap-2 font-display text-sm text-mp-darkgreen cursor-pointer">
                <FastForward size={16} className="text-mp-teal" />
                Auto-avansering
              </label>
              <button
                id="auto-advance"
                type="button"
                role="switch"
                aria-checked={autoAdvance}
                onClick={() => onAutoAdvanceChange(!autoAdvance)}
                className={`relative inline-flex h-6 w-11 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors focus:outline-none focus:ring-2 focus:ring-mp-magenta/40 focus:ring-offset-2 ${
                  autoAdvance ? 'bg-mp-magenta' : 'bg-mp-stone-200'
                }`}
              >
                <span
                  className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow-sm ring-0 transition ${
                    autoAdvance ? 'translate-x-5' : 'translate-x-0'
                  }`}
                />
              </button>
            </div>
          )}

          <div className="flex items-center justify-between gap-4 landscape:flex-1">
            <div className="flex-1 flex flex-col justify-center landscape:hidden">
              <p className="font-display text-xs text-mp-stone-500 ml-1 mb-1 uppercase tracking-[0.12em]">
                {canStart ? 'Klar til å spele?' : 'Ventar på spelarar …'}
              </p>
              <div className="h-1.5 w-full bg-mp-stone-200 rounded-full overflow-hidden">
                <div
                  className={`h-full rounded-full transition-all duration-500 ${canStart ? 'bg-mp-lime' : 'bg-mp-stone-300'}`}
                  style={{ width: canStart ? '100%' : '0%' }}
                />
              </div>
            </div>
            <button
              onClick={onStartGame}
              disabled={!canStart}
              className="mp-cta mp-cta-large shrink-0 landscape:flex-1 landscape:!py-3 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Play size={20} />
              Start spel
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
